import {
  Fragment,
  useId,
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react"
import classnames from "classnames"
import { cx } from "@/utils/strings"
import { NavLink, useParams, useNavigate } from "react-router-dom"
import LogoIcon from "../icons/LogoIcon"
import { LINKS } from "./constants"
import Select from "@/components/Select"
import ArrowDropdownIcon from "@/assets/arrow-drop-down.svg"
import OrganizationsSelect from "./OrganizationsSelect"
import { useEffectOnce } from "usehooks-ts"
import { filterOption } from "../Select/constants"
import { useStatusOrganizations } from "@/hooks/useStatusOrganizations"
import { useOrganizationPermissions } from "@/hooks/useOrganizationPermissions"
import { useAuth } from "@/hooks/useAuth"

type Props = {
  className?: string
  showLogo?: boolean
  onLinkChange?: () => void
}

const Navbar = ({ className, showLogo = true, onLinkChange }: Props) => {
  const orgSelectId = useId()
  const [searchTerm, setSearchTerm] = useState("")
  const textFieldRef = useRef<HTMLInputElement>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [renderId, setRenderId] = useState(1)
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAdmin, user } = useAuth()

  useEffectOnce(() => {
    document.getElementById(orgSelectId)?.remove() // this fixes search field not focusing
  })

  const handleDropdownOpenChange = (open: boolean) => {
    setRenderId((prev) => prev + 1)
    setDropdownOpen(open)
    setSearchTerm("")
  }
  const { currentOrgActive, active, disabled } = useStatusOrganizations()
  const organizations = currentOrgActive ? active : disabled

  // Get user permissions for organization
  const {
    accessiblePages,
    isLoading: permissionsLoading,
    autoSelectedOrgId,
  } = useOrganizationPermissions()
  const hasInboxingDomainsAccess = useMemo(
    () =>
      accessiblePages.some((page) =>
        page.toLowerCase().includes("inboxing-domains"),
      ),
    [accessiblePages],
  )
  const managedDomainFirst = Boolean(
    user?.isInboxingUser && !isAdmin && hasInboxingDomainsAccess,
  )

  // Auto-navigate to the first organization if one is auto-selected
  useEffect(() => {
    if (autoSelectedOrgId && !id) {
      navigate(
        managedDomainFirst
          ? `/organization/${autoSelectedOrgId}/inboxing-domains`
          : `/organization/${autoSelectedOrgId}/dashboard`,
      )
    }
  }, [autoSelectedOrgId, id, managedDomainFirst, navigate])

  // Filter links based on user permissions (RESTORE ORIGINAL LOGIC)
  const filteredLinks = useMemo(() => {
    // If permissions are still loading, show no links
    if (permissionsLoading) return []

    // Filter the links to only include those the user has access to
    return LINKS.filter((link) => {
      if (link.title === "Inbox" && !isAdmin) {
        return false
      }

      if (link.title === "Senders" && !isAdmin) {
        return false
      }

      if (link.title === "Advanced DNS" && !isAdmin) {
        return false
      }

      // Extract the page path from the route
      const pagePath = link.to?.split("?")[0]?.split("/")?.pop() || ""

      // Special case for dashboard which might be represented differently
      if (pagePath === "dashboard") {
        const hasDashboardAccess = accessiblePages.some(
          (p) =>
            p.includes("/dashboard") || p === "Dashboard" || p === "dashboard",
        )
        return hasDashboardAccess
      }

      // Check different variations of the page path
      const variations = [
        `/${pagePath}`,
        pagePath,
        pagePath.charAt(0).toUpperCase() + pagePath.slice(1), // Capitalized
        pagePath.replace(/-/g, " "), // Replace dashes with spaces
      ]

      const isAccessible = variations.some((variation) =>
        accessiblePages.some((p) => p.includes(variation)),
      )

      return isAccessible
    })
  }, [accessiblePages, isAdmin, permissionsLoading]) // Restore original dependencies

  const organizationsSelectOptions = [...organizations]
    .sort(
      (a, b) => Number(new Date(a.createdAt)) - Number(new Date(b.createdAt)),
    )
    .map(({ name, _id }) => ({
      label: name,
      value: _id,
    }))
  const selectedOrganization = organizations.find(({ _id }) => _id === id)
  const firstLegacyLinkIndex = filteredLinks.findIndex((link) => link.legacy)

  // Function to navigate to the first accessible page for the selected organization
  const navigateToFirstAccessiblePage = useCallback(
    (_id: string) => {
      if (!_id) return

      if (managedDomainFirst) {
        navigate(`/organization/${_id}/inboxing-domains`)
        onLinkChange && onLinkChange()
        return
      }

      // Use filteredLinks again
      const firstAccessibleLink = filteredLinks.find((link) => link.to)

      if (firstAccessibleLink) {
        const targetRoute = firstAccessibleLink.to.replace(":id", _id)
        navigate(targetRoute)
      } else {
        // Fallback to the organizations page if no accessible pages
        navigate("/organizations")
      }

      onLinkChange && onLinkChange()
    },
    [filteredLinks, managedDomainFirst, navigate, onLinkChange],
  ) // Restore original dependencies

  return (
    <nav
      className={cx(
        "hidden md:w-44 lg:w-64 bg-white border-r border-r-outline h-screen overflow-x-hidden overflow-auto px-2 pt-6 pb md:flex flex-col gap-6",
        className,
      )}
    >
      {showLogo && (
        <div className="self-center">
          <LogoIcon className="h-9 w-[146px]" />
        </div>
      )}
      <ul className="list-none flex flex-col gap-2 flex-1">
        {/* Use filteredLinks again */}
        {filteredLinks.map(({ Icon, title, to, legacy }, i) => (
          <NavLink
            to={to?.replace(":id", id!) ?? i.toString()}
            onClick={() => onLinkChange && onLinkChange()}
            key={i} // Restore original key
            className={({ isActive }) =>
              classnames(
                "flex items-center gap-2 cursor-pointer p-2 rounded-md text-black-secondary hover:text-black-secondary hover:bg-primary-outline",
                legacy && "mt-1 text-text-light opacity-70",
                legacy && i === firstLegacyLinkIndex && "mt-auto",
                {
                  "!bg-primary !text-white opacity-100": isActive,
                  "hover:opacity-100": legacy,
                },
              )
            }
          >
            <Icon className="w-6 h-6" />
            <span className="font-medium text-sm">{title}</span>
          </NavLink>
        ))}
        {permissionsLoading && (
          <div className="text-sm text-gray-400 p-2">Loading navigation...</div>
        )}
        {!permissionsLoading && filteredLinks.length === 0 && (
          <div className="text-sm text-gray-400 p-2">No accessible pages</div>
        )}
      </ul>
      {/* Restore unconditional rendering of Org Select */}
      <div className="py-4 flex flex-col gap-2">
        <Select
          id={orgSelectId}
          options={organizationsSelectOptions}
          searchValue={searchTerm}
          dropdownRender={(menu) => (
            <Fragment key={renderId}>
              <OrganizationsSelect
                searchTerm={searchTerm}
                onSearch={setSearchTerm}
                menu={menu}
                textFieldRef={textFieldRef}
              />
            </Fragment>
          )}
          open={dropdownOpen}
          onDropdownVisibleChange={handleDropdownOpenChange}
          className="h-11"
          popupMatchSelectWidth={true}
          placeholder="Select organization"
          suffixIcon={<ArrowDropdownIcon />}
          value={selectedOrganization?._id ?? ""}
          onChange={(_id) => navigateToFirstAccessiblePage(_id)}
          filterOption={filterOption}
        />
      </div>
    </nav>
  )
}

export default Navbar
